import { useRouter } from 'next/router';
import { type FC, useContext, useEffect } from 'react';

import Buttons from '@/components/Buttons';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import CreditCard from '@/components/Icons/CreditCard';
import * as format from '@/format';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import UserProfileContext from '@/layouts/UserProfileLayout/UserProfileContext';
import { useOpenInvoicePane } from '@/panes/hooks';
import { type PaymentGateRead } from '@/tsClient/servers/noodleApi/ApiDefinition/workflows';
import { type Currency, DEFAULT_CURRENCY } from '@/typings/common';

import s from './PaymentGateCard.module.scss';

const computeProgressRatio = (gate: PaymentGateRead): number => {
  if (gate.status === 'completed') return 1;
  if (gate.gateType === 'minimum' && gate.minimumAmount) {
    return Math.min(gate.invoicePaidAmount / gate.minimumAmount, 1);
  }
  if (gate.gateType === 'percentage' && gate.minimumPercentage && gate.invoiceTotalAmount) {
    const paidPct = (gate.invoicePaidAmount / gate.invoiceTotalAmount) * 100;
    return Math.min(paidPct / gate.minimumPercentage, 1);
  }
  return 0;
};

const renderSubtext = (gate: PaymentGateRead, currency: Currency): React.ReactNode => {
  if (gate.status === 'completed') return 'Payment received';
  if (gate.gateType === 'minimum' && gate.minimumAmount) {
    return (
      <>
        {format.price.withCurrency(gate.invoicePaidAmount, currency)} of{' '}
        {format.price.withCurrency(gate.minimumAmount, currency)} minimum
      </>
    );
  }
  if (gate.gateType === 'percentage' && gate.minimumPercentage) {
    return <>{gate.minimumPercentage}% of total invoice required before proceeding</>;
  }
  return null;
};

type Props = {
  gate: PaymentGateRead;
  canAccess: boolean;
  creatorSlug?: string;
  currency?: Currency;
  setIsFulfilled?: (f: boolean) => void;
};

const PaymentGateCard: FC<Props> = ({ gate, canAccess, creatorSlug, currency = DEFAULT_CURRENCY, setIsFulfilled }) => {
  const router = useRouter();
  const { creator, isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);
  const openInvoicePane = useOpenInvoicePane();

  const isCompleted = gate.status === 'completed';
  const ratio = computeProgressRatio(gate);
  const offset = (1 - ratio) * 138;

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  const handleClick = (): void => {
    if (isInDashboard || isInUserProfile) {
      openInvoicePane({ invoiceId: gate.invoiceId });
      return;
    }
    void router.push(
      getUrl(IDENTIFIERS.INVOICE_DETAIL, {
        creatorSlug: creatorSlug || creator?.slug || (router.query.creatorSlug as string) || '',
        invoiceId: gate.invoiceId,
      }),
    );
  };

  return (
    <Buttons className={s.container} key={gate.id} isFullWidth isWrapper onClick={handleClick} disabled={!canAccess}>
      {isCompleted ? (
        <CheckCircle size={16} weight='fill' color='var(--color-success)' />
      ) : (
        <figure>
          <svg className={s.progress} width={36} height={36} viewBox='0 0 46 46'>
            <circle
              cx={23}
              cy={23}
              r={22}
              style={{
                strokeDasharray: '138',
                strokeDashoffset: `${offset}`,
              }}
            />
          </svg>
          <CreditCard size={20} color='var(--color-gray-100)' />
        </figure>
      )}
      <div>
        <p
          className={isCompleted ? 'caption-bold' : 'body-sm-bold'}
          style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
        >
          Payment Required
        </p>
        <p className='caption'>{renderSubtext(gate, currency)}</p>
      </div>
      {canAccess && (
        <>{isCompleted ? <CaretRight size={12} color='var(--color-gray-100)' /> : <div className={s.open}>View</div>}</>
      )}
    </Buttons>
  );
};

export default PaymentGateCard;
